import { useEffect, useRef, useState } from 'react';

import { FileImage } from '@/common/constants';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';

interface Props {
  storeKey: string;
}

const useSynchronizeFilesInDocumentStore = ({ storeKey }: Props) => {
  const initialFilesRef = useRef<FileImage[]>([]);
  const [isReady, setIsReady] = useState(false);

  const {
    values: { documentId, files },
    handleSetValues: synchronizeFilesInDocumentStore,
  } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  const {
    values: { documentId: newDocumentId, files: newFiles },
    handleSetValues,
  } = useFilesInDocumentStore(storeKey);

  useEffect(() => {
    if (documentId && !newDocumentId) {
      handleSetValues({ documentId });
    }
  }, [documentId, newDocumentId, handleSetValues]);

  useEffect(() => {
    if (!documentId && newDocumentId) {
      synchronizeFilesInDocumentStore({
        documentId: newDocumentId,
      });
    }
    setIsReady(true);
  }, [documentId, newDocumentId, synchronizeFilesInDocumentStore]);

  useEffect(() => {
    initialFilesRef.current = files;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialFiles = initialFilesRef.current;

    const initialIds = new Set(initialFiles.map(file => file.id));

    const synchronizedFiles = [
      ...initialFiles,
      ...newFiles.filter(file => !initialIds.has(file.id)),
    ];

    const isChanged =
      synchronizedFiles.length !== files.length ||
      synchronizedFiles.some((file, index) => file.id !== files[index]?.id);

    if (isChanged) {
      synchronizeFilesInDocumentStore({
        files: synchronizedFiles,
      });
    }
  }, [files, newFiles, synchronizeFilesInDocumentStore]);

  return {
    isReady,
  };
};

export default useSynchronizeFilesInDocumentStore;
